// src/hooks/usePowerAnalysisData.ts
import { useCallback, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useChartPropsPattern } from '../utils/chartPropsPattern';
import {
  fetchPhysicsBasedPowerTimeSeriesWithMeta,
  fetchSwivelPowerTimeSeriesData,
  PowerTimeSeriesData,
} from '../utils/chartDataFetchers';
import { LeverMode } from '../config/experiments';
import { useCalibratedPhysicsWaveform } from './useCalibratedPhysicsWaveform';
import { applyPowerCorrection } from '../utils/parameterCorrections';
import { useErrorTracking } from './useErrorTracking';
import type { RowerContext } from '../types/rowerContext';

interface UsePowerAnalysisDataProps {
  sessionId: string;
  parameterInfo: any;
  dataMode: 'periodic' | 'aperiodic';
  leverMode: LeverMode;
  rowerContext?: RowerContext | null;
}

export function usePowerAnalysisData({
  sessionId,
  parameterInfo,
  dataMode,
  leverMode,
  rowerContext,
}: UsePowerAnalysisDataProps) {
  const { reportError } = useErrorTracking('PowerAnalysisData');

  // Memoize the data fetcher to prevent infinite loops
  // Adapter: always return series to the hook (no meta), keep stroke boundaries from a separate query
  const memoizedSeriesFetcher = useCallback(
    async (sessionId: string) => {
      if (dataMode === 'periodic') {
        const meta = await fetchPhysicsBasedPowerTimeSeriesWithMeta(sessionId, rowerContext);
        return meta.data || [];
      }
      return (await fetchSwivelPowerTimeSeriesData(sessionId)) || [];
    },
    [dataMode, rowerContext]
  );

  // Memoize the options to prevent infinite loops
  const memoizedOptions = useMemo(
    () => ({
      enablePerformanceMode: true,
      retryAttempts: 2,
    }),
    []
  );

  // Use ChartProps pattern for data fetching and parameter corrections
  // Enhanced with React Query caching for better performance
  const {
    data: powerData,
    loading,
    error,
    corrections,
    correctionDisplay,
    retry,
    shouldUseDecimation,
  } = useChartPropsPattern<PowerTimeSeriesData[]>(sessionId, parameterInfo, memoizedSeriesFetcher, {
    ...memoizedOptions,
    queryKey: ['power-analysis-chart', dataMode, leverMode],
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  // Separate query for stroke boundaries (periodic only)
  const { data: strokeBounds = [] } = useQuery({
    queryKey: ['power-analysis-strokes', sessionId, dataMode, rowerContext?.position],
    enabled: dataMode === 'periodic' && !!sessionId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    queryFn: async () => {
      const meta = await fetchPhysicsBasedPowerTimeSeriesWithMeta(sessionId, rowerContext);
      return meta.strokeBoundaries || [];
    },
  });

  // Use calibrated physics waveform hook for periodic mode
  const {
    calibratedData,
    alpha,
    method,
    hasCalibration,
    refetchCalibration,
    domainMax,
    ready: calibrationReady,
  } = useCalibratedPhysicsWaveform(sessionId, (powerData as PowerTimeSeriesData[]) || []);

  // Apply parameter corrections to the data (after calibration) - EXACT COPY from original
  const correctedData = useMemo(() => {
    const sourceData = (dataMode === 'periodic' ? calibratedData : powerData) as any;
    if (!sourceData || !Array.isArray(sourceData)) return sourceData;

    // Apply parameter corrections if enhanced
    if (corrections.isEnhanced && corrections.correction !== 1) {
      return sourceData.map((point) => {
        const correctedPoint = { ...point };
        Object.keys(point).forEach((key) => {
          if (key !== 'time' && typeof point[key] === 'number') {
            const originalValue = point[key] as number;
            const correctedValue = applyPowerCorrection(originalValue, corrections.correction);
            correctedPoint[key] = correctedValue;
          }
        });
        return correctedPoint;
      });
    }

    return sourceData;
  }, [calibratedData, powerData, dataMode, corrections.isEnhanced, corrections.correction]);

  // Report errors to Sentry
  useEffect(() => {
    if (error) {
      reportError(new Error(error), {
        context: 'power_analysis_chart_error',
        sessionId,
        chartType: 'power_analysis',
      });
    }
  }, [error, sessionId, reportError]);

  return {
    // Core data
    powerData,
    strokeBounds,
    loading,
    error,
    retry,
    shouldUseDecimation,

    // Corrections
    corrections,
    correctionDisplay,

    // Processed data (our main value-add)
    correctedData,

    // Calibration data (expose from inner hook for UI sync)
    alpha,
    method,
    hasCalibration,
    refetchCalibration,
    domainMax,
    calibrationReady,
  };
}
